import { useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { useApp } from "../context/AppContext";

export const CHIP = {
  "Pending":         {bg:"#fef3e2",c:"#d97706",icon:"⏳"},
  "In Process":      {bg:"#e0ecff",c:"#2563eb",icon:"🔄"},
  "Approved":        {bg:"#dcfce7",c:"#16a34a",icon:"✅"},
  "Rejected":        {bg:"#fee2e2",c:"#dc2626",icon:"❌"},
  "Modify Required": {bg:"#fff3d6",c:"#b45309",icon:"✏️"},
};
export const STATUS_OPTIONS = ["Pending","In Process","Approved","Rejected","Modify Required"];

/* NOTE: Section/Grid/DocCard are defined OUTSIDE the page component (module scope).
   Defining them inside the component used to break the textarea — every keystroke
   re-created these as "new" components, which forced React to unmount & remount
   the whole block (including the textarea), so typing never registered properly. */

function Section({icon,title,children}) {
  return (
    <div style={{background:"#fff",borderRadius:14,border:"1px solid #e4e9f0",overflow:"hidden",marginBottom:20}}>
      <div style={{padding:"18px 24px",borderBottom:"1px solid #eaeef5",display:"flex",alignItems:"center",gap:12,background:"#fafbfd"}}>
        <div style={{width:38,height:38,borderRadius:10,background:"linear-gradient(135deg,#4a7fb5,#d4736a)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>{icon}</div>
        <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:600,color:"#0d1f3c"}}>{title}</h2>
      </div>
      <div style={{padding:24}}>{children}</div>
    </div>
  );
}

function Grid({items}) {
  return (
    <div className="rgrid-3" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
      {items.map(([l,v])=>(
        <div key={l} style={{background:"#f6f8fc",borderRadius:10,padding:"13px 15px"}}>
          <div style={{fontSize:10,letterSpacing:"1.5px",textTransform:"uppercase",color:"#8a9bb5",fontWeight:600}}>{l}</div>
          <div style={{fontSize:13,color:"#1a2540",fontWeight:500,marginTop:4}}>{v||"—"}</div>
        </div>
      ))}
    </div>
  );
}

function buildReviewLink(appId) {
  return `${window.location.origin}${window.location.pathname}?page=applicant-review&app_id=${appId}`;
}

function ReviewLinkButton({ appId }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    const link = buildReviewLink(appId);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(()=>setCopied(false), 2200);
    } catch {
      window.prompt("Copy this review link:", link);
    }
  };
  return (
    <button onClick={handleCopy} style={{padding:"9px 16px",borderRadius:9,fontSize:12,fontWeight:600,background:copied?"#dcfce7":"#eaeef5",color:copied?"#16a34a":"#1a2540",border:"none",cursor:"pointer"}}>
      {copied ? "✓ Link Copied!" : "🔗 Copy Review Link for Applicant"}
    </button>
  );
}

function Stars({rating}) {
  return <span style={{color:"#f5b301",fontSize:18,letterSpacing:2}}>{"★".repeat(rating)}<span style={{color:"#dde3ed"}}>{"★".repeat(5-rating)}</span></span>;
}

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

async function copyImageToClipboard(dataUrl) {
  try {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })]);
    return true;
  } catch {
    return false;
  }
}

function DocCard({label, dataUrl, filename}) {
  const [copied, setCopied] = useState(false);
  const isImage = dataUrl && dataUrl.startsWith("data:image");

  const handleCopy = async () => {
    const ok = await copyImageToClipboard(dataUrl);
    if (ok) { setCopied(true); setTimeout(()=>setCopied(false), 2000); }
    else alert("Copy to clipboard isn't supported in this browser. Please use Download instead.");
  };

  return (
    <div style={{textAlign:"center"}}>
      {isImage ? (
        <img src={dataUrl} alt={label} style={{width:110,height:110,objectFit:"cover",borderRadius:12,border:"1px solid #e4e9f0"}}/>
      ) : (
        <div style={{width:110,height:110,borderRadius:12,border:"1px solid #e4e9f0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:34,background:"#f6f8fc"}}>📄</div>
      )}
      <div style={{fontSize:11,color:"#8a9bb5",marginTop:6}}>{label}</div>
      <div style={{display:"flex",gap:6,justifyContent:"center",marginTop:8}}>
        <button onClick={()=>downloadDataUrl(dataUrl, filename)} style={{padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:600,background:"#e0ecff",color:"#2563eb",border:"none",cursor:"pointer"}}>⬇ Download</button>
        {isImage && (
          <button onClick={handleCopy} style={{padding:"5px 10px",borderRadius:7,fontSize:11,fontWeight:600,background:copied?"#dcfce7":"#eaeef5",color:copied?"#16a34a":"#1a2540",border:"none",cursor:"pointer"}}>{copied ? "✓ Copied" : "📋 Copy"}</button>
        )}
      </div>
    </div>
  );
}

export default function AdminView({ navigate, params }) {
  const { getApp, updateStatus, refresh } = useApp();
  const app = getApp(params?.app_id);

  const [selStatus, setSelStatus] = useState(app?.status || "Pending");
  const [note,      setNote]      = useState(app?.statusNote || "");
  const [saved,      setSaved]    = useState(false);
  const [warn,        setWarn]    = useState("");

  const needsNote = selStatus === "Rejected" || selStatus === "Modify Required";

  const handleSave = () => {
    if (needsNote && !note.trim()) { setWarn("Please add a reason for this status."); return; }
    updateStatus(app.id, selStatus, needsNote ? note.trim() : "");
    setWarn(""); setSaved(true);
    setTimeout(()=>setSaved(false), 2500);
  };

  if (!app) return (
    <AdminLayout navigate={navigate} active="dashboard" title="Application Not Found">
      <div style={{textAlign:"center",padding:60}}>
        <div style={{fontSize:48,marginBottom:16}}>❌</div>
        <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,color:"#0d1f3c",marginBottom:12}}>Application Not Found</h2>
        <button onClick={()=>navigate("dashboard")} style={{padding:"12px 24px",background:"linear-gradient(135deg,#4a7fb5,#d4736a)",color:"#fff",border:"none",borderRadius:10,fontSize:13,fontWeight:600,cursor:"pointer"}}>← Back to Dashboard</button>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout navigate={navigate} active="dashboard" title="View Application">
      {/* HEADER */}
      <div style={{background:"#0d1f3c",borderRadius:16,padding:"28px 32px",marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:16}}>
        <div>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:30,fontWeight:700,color:"#fff",marginBottom:4}}>{app.full_name}</h1>
          <p style={{fontSize:12,color:"#8a9bb5"}}>Submitted {app.submitted_at} · Agent: {app.agent_name} ({app.agent_agency})</p>
        </div>
        <div style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(74,127,181,0.3)",borderRadius:12,padding:"14px 22px",textAlign:"right"}}>
          <div style={{fontSize:10,letterSpacing:2,color:"#4a7fb5",textTransform:"uppercase",marginBottom:4}}>Reference ID</div>
          <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:700,color:"#fff",letterSpacing:2}}>{app.id}</div>
        </div>
      </div>

      {/* CURRENT STATUS */}
      <div style={{display:"flex",gap:12,marginBottom:20,flexWrap:"wrap",alignItems:"center"}}>
        <span style={{display:"inline-flex",alignItems:"center",gap:6,padding:"9px 18px",borderRadius:10,fontSize:13,fontWeight:600,background:CHIP[app.status]?.bg,color:CHIP[app.status]?.c}}>
          {CHIP[app.status]?.icon} {app.status}
        </span>
        {app.statusNote && (
          <span style={{fontSize:12,color:"#6b7a90"}}>Note: <strong style={{color:"#1a2540"}}>{app.statusNote}</strong></span>
        )}
        <button onClick={()=>navigate("dashboard")} style={{marginLeft:"auto",padding:"9px 18px",borderRadius:10,fontSize:13,fontWeight:600,background:"#eaeef5",color:"#1a2540",border:"none",cursor:"pointer"}}>← Back to Dashboard</button>
      </div>

      <div style={{marginBottom:20,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <ReviewLinkButton appId={app.id}/>
        <button onClick={refresh} style={{padding:"9px 14px",borderRadius:9,fontSize:12,fontWeight:600,background:"#eaeef5",color:"#1a2540",border:"none",cursor:"pointer"}}>🔄 Refresh</button>
        <span style={{fontSize:11,color:"#8a9bb5"}}>Send this link via WhatsApp/Email. If the applicant just submitted a review and it's not showing, click Refresh.</span>
      </div>

      {app.review && (
        <Section icon="⭐" title="Applicant Review">
          <Stars rating={app.review.rating}/>
          <div style={{fontSize:11,color:"#8a9bb5",marginTop:4,marginBottom:10}}>Submitted {app.review.submittedAt}</div>
          {app.review.comment && <p style={{fontSize:13,color:"#1a2540",fontStyle:"italic",background:"#f6f8fc",padding:"12px 14px",borderRadius:10}}>"{app.review.comment}"</p>}
        </Section>
      )}

      {/* UPDATE STATUS */}
      <Section icon="🔄" title="Update Application Status">
        <div style={{display:"flex",gap:14,flexWrap:"wrap",alignItems:"flex-start"}}>
          <div>
            <label style={{fontSize:11,fontWeight:600,letterSpacing:"1.5px",textTransform:"uppercase",color:"#6b7a90",marginBottom:6,display:"block"}}>Status</label>
            <select value={selStatus} onChange={e=>{setSelStatus(e.target.value); setWarn("");}}
              style={{padding:"11px 16px",border:"1.5px solid #dde3ed",borderRadius:10,fontSize:13,fontFamily:"'DM Sans',sans-serif",color:"#0d1f3c",background:"#fafbfd",outline:"none",cursor:"pointer",minWidth:180}}>
              {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {needsNote && (
            <div style={{flex:1,minWidth:220}}>
              <label style={{fontSize:11,fontWeight:600,letterSpacing:"1.5px",textTransform:"uppercase",color:"#6b7a90",marginBottom:6,display:"block"}}>
                Reason {selStatus==="Rejected" ? "for Rejection" : "/ What needs to be modified"} <span style={{color:"#c9a227"}}>*</span>
              </label>
              <textarea value={note} onChange={e=>{setNote(e.target.value); setWarn("");}} rows={2} placeholder="Explain the reason — this will be shown to the agent."
                style={{width:"100%",padding:"11px 14px",border:"1.5px solid #dde3ed",borderRadius:10,fontSize:13,fontFamily:"'DM Sans',sans-serif",color:"#0d1f3c",background:"#fafbfd",outline:"none",resize:"vertical",boxSizing:"border-box"}}/>
            </div>
          )}
          <div style={{alignSelf:"flex-end"}}>
            <button onClick={handleSave} style={{padding:"11px 22px",borderRadius:10,fontSize:13,fontWeight:600,background:"linear-gradient(135deg,#4a7fb5,#d4736a)",color:"#fff",border:"none",cursor:"pointer"}}>💾 Save Status</button>
          </div>
        </div>
        {warn && <div style={{marginTop:10,fontSize:12,color:"#dc2626"}}>⚠ {warn}</div>}
        {saved && <div style={{marginTop:10,fontSize:12,color:"#16a34a"}}>✓ Status updated — agent will see this on their dashboard.</div>}
      </Section>

      {(app.passport_photo || app.passport_copy) && (
        <Section icon="🪪" title="Uploaded Documents">
          <div style={{display:"flex",gap:24,flexWrap:"wrap"}}>
            {app.passport_photo && <DocCard label="Applicant Photo" dataUrl={app.passport_photo} filename={app.passport_photo_name || `${app.id}-photo.jpg`}/>}
            {app.passport_copy  && <DocCard label="Passport Copy"   dataUrl={app.passport_copy}  filename={app.passport_copy_name  || `${app.id}-passport.jpg`}/>}
          </div>
        </Section>
      )}

      <Section icon="🎯" title="Service Requested">
        <Grid items={[["Service Type",app.service_type],["Submission Time",app.submitted_at],["Agent Email",app.agent_email]]}/>
      </Section>

      <Section icon="👤" title="Personal Information">
        <Grid items={[["Full Name",app.full_name],["Date of Birth",app.dob],["Gender",app.gender],["Nationality",app.nationality],["Passport Number",app.passport_no],["Passport Expiry",app.passport_expiry],["Email Address",app.email],["Phone",app.phone],["Address",app.address]]}/>
      </Section>

      <Section icon="🗺️" title="Travel Details">
        <Grid items={[["Destination",app.destination],["Travel Purpose",app.travel_purpose],["Travel Date",app.travel_date],["Return Date",app.return_date],["Preferred Airline",app.airline_pref],["Hotel Category",app.hotel_category],["Visa Type",app.visa_type],["Special Requirements",app.special_req||"None"]]}/>
      </Section>
    </AdminLayout>
  );
}
